import { useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Lock, Check } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Separator } from '@/components/ui/separator'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useToast } from '@/components/ui/use-toast'

const plans = {
  starter: {
    name: 'Starter',
    monthlyPrice: 19,
    yearlyPrice: 190,
    features: [
      'Up to 5 team members',
      '100 templates per month',
      '1,000 AI generations',
      '10 GB storage',
      'Basic support',
    ],
  },
  professional: {
    name: 'Professional',
    monthlyPrice: 49,
    yearlyPrice: 490,
    features: [
      'Up to 20 team members',
      'Unlimited templates',
      '10,000 AI generations',
      '100 GB storage',
      'Priority support',
      'Custom branding',
      'API access',
      'Advanced analytics',
    ],
  },
  enterprise: {
    name: 'Enterprise',
    monthlyPrice: 199,
    yearlyPrice: 1990,
    features: [
      'Unlimited team members',
      'Unlimited templates',
      'Unlimited AI generations',
      '1 TB storage',
      'Dedicated support',
      'Custom branding',
      'Full API access',
      'Advanced analytics',
      'SSO & SAML',
      'Custom integrations',
      'SLA guarantee',
    ],
  },
}

export default function BillingCheckout() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { toast } = useToast()

  const planId = searchParams.get('plan') as keyof typeof plans || 'professional'
  const interval = searchParams.get('interval') as 'month' | 'year' || 'month'

  const [processing, setProcessing] = useState(false)
  const [paymentMethod, setPaymentMethod] = useState('saved')
  const [formData, setFormData] = useState({
    cardNumber: '',
    cardName: '',
    expiryDate: '',
    cvv: '',
    billingEmail: 'john.doe@example.com',
  })

  const plan = plans[planId]
  const price = interval === 'year' ? plan.yearlyPrice : plan.monthlyPrice
  const discount = interval === 'year' ? plan.monthlyPrice * 12 - plan.yearlyPrice : 0
  const tax = price * 0.1 // 10% tax
  const total = price + tax

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setProcessing(true)

    try {
      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 2000))

      toast({
        title: 'Success!',
        description: 'Your subscription has been updated successfully.',
      })

      navigate('/billing?success=true')
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Failed to process payment. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setProcessing(false)
    }
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/billing">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Billing
          </Link>
        </Button>
      </div>

      <div>
        <h1 className="text-3xl font-bold tracking-tight">Checkout</h1>
        <p className="text-gray-500 dark:text-gray-400">
          Complete your subscription upgrade
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Payment Method</CardTitle>
              <CardDescription>
                Choose how you'd like to pay for your subscription
              </CardDescription>
            </CardHeader>
            <CardContent>
              <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod}>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <RadioGroupItem value="saved" id="saved" />
                    <label htmlFor="saved" className="flex-1 cursor-pointer">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">Visa ending in 4242</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Expires 12/2025
                          </p>
                        </div>
                        <div className="h-8 w-12 rounded bg-gradient-to-r from-blue-600 to-blue-400" />
                      </div>
                    </label>
                  </div>

                  <div className="flex items-start space-x-3">
                    <RadioGroupItem value="new" id="new" />
                    <label htmlFor="new" className="flex-1 cursor-pointer">
                      <p className="font-medium">Add new payment method</p>
                    </label>
                  </div>
                </div>
              </RadioGroup>

              {paymentMethod === 'new' && (
                <div className="mt-6 space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="cardNumber">Card Number</Label>
                    <Input
                      id="cardNumber"
                      placeholder="1234 5678 9012 3456"
                      value={formData.cardNumber}
                      onChange={(e) => setFormData({ ...formData, cardNumber: e.target.value })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="cardName">Name on Card</Label>
                    <Input
                      id="cardName"
                      placeholder="John Doe"
                      value={formData.cardName}
                      onChange={(e) => setFormData({ ...formData, cardName: e.target.value })}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="expiryDate">Expiry Date</Label>
                      <Input
                        id="expiryDate"
                        placeholder="MM/YY"
                        value={formData.expiryDate}
                        onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cvv">CVV</Label>
                      <Input
                        id="cvv"
                        placeholder="123"
                        value={formData.cvv}
                        onChange={(e) => setFormData({ ...formData, cvv: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Billing Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="billingEmail">Billing Email</Label>
                <Input
                  id="billingEmail"
                  type="email"
                  value={formData.billingEmail}
                  onChange={(e) => setFormData({ ...formData, billingEmail: e.target.value })}
                />
              </div>
              <Alert>
                <AlertDescription>
                  Invoices and receipts will be sent to this email address.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Order Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="font-semibold">{plan.name} Plan</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Billed {interval}ly
                </p>
              </div>

              <Separator />

              <div className="space-y-2 text-sm">
                {plan.features.slice(0, 5).map((feature, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                    <span>{feature}</span>
                  </div>
                ))}
                {plan.features.length > 5 && (
                  <p className="text-gray-500 dark:text-gray-400 pl-6">
                    +{plan.features.length - 5} more features
                  </p>
                )}
              </div>

              <Separator />

              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">Subtotal</span>
                  <span>${price.toFixed(2)}</span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between text-green-600 dark:text-green-400">
                    <span>Annual discount</span>
                    <span>-${discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">Tax</span>
                  <span>${tax.toFixed(2)}</span>
                </div>
                <Separator />
                <div className="flex justify-between font-semibold text-lg">
                  <span>Total</span>
                  <span>${total.toFixed(2)}</span>
                </div>
              </div>
            </CardContent>
            <CardFooter>
              <form onSubmit={handleSubmit} className="w-full">
                <Button
                  type="submit"
                  className="w-full"
                  size="lg"
                  disabled={processing}
                >
                  {processing ? (
                    'Processing...'
                  ) : (
                    <>
                      <Lock className="mr-2 h-4 w-4" />
                      Pay ${total.toFixed(2)}
                    </>
                  )}
                </Button>
              </form>
            </CardFooter>
          </Card>

          <div className="text-center text-sm text-gray-500 dark:text-gray-400">
            <p className="flex items-center justify-center gap-2">
              <Lock className="h-4 w-4" />
              Secure payment powered by Stripe
            </p>
            <p className="mt-2">
              By subscribing, you agree to our Terms of Service
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
